import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  CheckCircle2,
  Edit2,
  Lock,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import AlertModal from '../modals/AlertModal';
import ConfirmModal from '../modals/ConfirmModal';
import {
  emitGovernanceTelemetry,
  governanceFailureReason,
} from '../utils/governanceTelemetry';
import './GarageDepartmentsAdminPanel.css';

const EMPTY_FORM = {
  value: '',
  label: '',
  description: '',
  parentId: '',
  initialAdminEmail: '',
  isActive: true,
};

const normalize = (value) => String(value || '').trim().toLowerCase();

const GarageDepartmentsAdminPanel = ({ userRole, users = [], onChanged }) => {
  const canManage = userRole === 'admin';
  const [departments, setDepartments] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirm, setConfirm] = useState({ isOpen: false });
  const [alert, setAlert] = useState({ isOpen: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [departmentRows, personaRows] = await Promise.all([
        garageAPI.getConfigOptions('department', true),
        garageAPI.getConfigOptions('persona', true),
      ]);
      setDepartments(departmentRows || []);
      setPersonas(personaRows || []);
    } catch (error) {
      setAlert({
        isOpen: true,
        title: 'Unable to load departments',
        message: error.response?.data?.detail || error.message || 'Please try again.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const personaById = useMemo(
    () => Object.fromEntries(personas.map((persona) => [persona.id, persona])),
    [personas]
  );

  const countsByDepartment = useMemo(() => {
    const counts = {};
    departments.forEach((department) => {
      const keys = new Set([normalize(department.value), normalize(department.label)]);
      counts[department.id] = users.reduce(
        (total, user) => {
          const userDepartment = normalize(user.department);
          if (!keys.has(userDepartment)) return total;
          total.users += 1;
          if (user.role === 'persona_admin') total.admins += 1;
          if (user.role === 'analyst_reviewer') total.reviewers += 1;
          return total;
        },
        { users: 0, admins: 0, reviewers: 0 }
      );
    });
    return counts;
  }, [departments, users]);

  const visibleDepartments = useMemo(() => {
    const needle = normalize(query);
    return departments.filter((department) => {
      if (status === 'active' && !department.isActive) return false;
      if (status === 'inactive' && department.isActive) return false;
      if (!needle) return true;
      const persona = personaById[department.parentId];
      return [department.label, department.value, department.description, persona?.label]
        .some((value) => normalize(value).includes(needle));
    });
  }, [departments, personaById, query, status]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    emitGovernanceTelemetry('admin_department_creation_started', {
      actorType: userRole,
    });
  };

  const openEdit = (department) => {
    setEditing(department);
    setForm({
      value: department.value || '',
      label: department.label || '',
      description: department.description || '',
      parentId: department.parentId || '',
      initialAdminEmail: '',
      isActive: department.isActive,
    });
    setShowForm(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (
      !form.label.trim()
      || !form.description.trim()
      || (!editing && (!form.value.trim() || !form.parentId || !form.initialAdminEmail))
    ) return;
    setSaving(true);
    let createdDepartment = null;
    try {
      const payload = editing
        ? {
            label: form.label.trim(),
            description: form.description.trim(),
            expectedUpdatedAt: editing.updatedAt,
          }
        : {
            category: 'department',
            value: form.value.trim(),
            label: form.label.trim(),
            description: form.description.trim(),
            parentId: form.parentId,
            isActive: form.isActive,
            displayOrder: departments.length + 1,
          };
      if (editing) {
        await garageAPI.updateConfigOption(editing.id, payload);
        [
          editing.label !== payload.label ? 'name' : null,
          (editing.description || '') !== (payload.description || '') ? 'description' : null,
        ].filter(Boolean).forEach((changedField) => {
          emitGovernanceTelemetry('admin_department_updated', {
            actorType: userRole,
            departmentId: editing.id,
            outcome: 'succeeded',
            changedField,
          });
        });
      } else {
        createdDepartment = await garageAPI.createConfigOption(payload);
        try {
          const initialAdmin = users.find(
            (user) => user.userEmail === form.initialAdminEmail
          );
          const existingPersonas = initialAdmin?.accessPersonas
            || (initialAdmin?.accessPersona ? [initialAdmin.accessPersona] : []);
          const departmentPersona = personaById[form.parentId]?.value || null;
          const accessPersonas = [
            ...new Set([...existingPersonas, departmentPersona].filter(Boolean)),
          ];
          await garageAPI.updateUserProfile(form.initialAdminEmail, {
            role: 'persona_admin',
            accessPersona: accessPersonas[0] || null,
            accessPersonas,
            expectedRole: initialAdmin?.role || 'user',
            expectedAccessPersona: initialAdmin?.accessPersona || null,
            expectedAccessPersonas: existingPersonas,
          });
        } catch (assignmentError) {
          // Keep a failed two-step create out of active assignment dropdowns.
          await garageAPI.updateConfigOption(createdDepartment.id, {
            isActive: false,
            expectedUpdatedAt: createdDepartment.updatedAt,
          });
          throw assignmentError;
        }
        emitGovernanceTelemetry('admin_department_creation_succeeded', {
          actorType: userRole,
          departmentId: createdDepartment.id,
          outcome: 'succeeded',
        });
      }
      setShowForm(false);
      await load();
      onChanged?.();
      setAlert({
        isOpen: true,
        title: editing ? 'Department updated' : 'Department created',
        message: editing
          ? `${payload.label} was updated.`
          : `${payload.label} was created and its initial Persona Admin was assigned.`,
        variant: 'success',
      });
    } catch (error) {
      emitGovernanceTelemetry(
        editing
          ? 'admin_department_updated'
          : 'admin_department_creation_failed',
        {
          actorType: userRole,
          departmentId: editing?.id || createdDepartment?.id,
          outcome: 'failed',
          failureReason: governanceFailureReason(error),
        }
      );
      setAlert({
        isOpen: true,
        title: createdDepartment ? 'Initial administrator not assigned' : 'Department not saved',
        message: createdDepartment
          ? `${error.response?.data?.detail || error.message || 'Assignment failed.'} The new department was left inactive so it cannot be used accidentally.`
          : error.response?.data?.detail || error.message || 'Please try again.',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const requestStatusChange = (department) => {
    const nextActive = !department.isActive;
    setConfirm({
      isOpen: true,
      title: nextActive ? 'Reactivate department?' : 'Deactivate department?',
      message: nextActive
        ? `${department.label} will be available for new role assignments and department tagging again.`
        : `${department.label} and its history will be preserved, but it will no longer be available for new scoped assignments until reactivated.`,
      confirmText: nextActive ? 'Reactivate' : 'Deactivate',
      variant: nextActive ? 'default' : 'warning',
      onConfirm: async () => {
        try {
          await garageAPI.updateConfigOption(department.id, {
            isActive: nextActive,
            expectedUpdatedAt: department.updatedAt,
          });
          emitGovernanceTelemetry(
            nextActive
              ? 'admin_department_reactivated'
              : 'admin_department_deactivated',
            {
              actorType: userRole,
              departmentId: department.id,
              outcome: 'succeeded',
              changedField: 'status',
            }
          );
          setConfirm({ isOpen: false });
          await load();
          onChanged?.();
        } catch (error) {
          emitGovernanceTelemetry(
            nextActive
              ? 'admin_department_reactivated'
              : 'admin_department_deactivated',
            {
              actorType: userRole,
              departmentId: department.id,
              outcome: 'failed',
              failureReason: governanceFailureReason(error),
              changedField: 'status',
            }
          );
          setConfirm({ isOpen: false });
          setAlert({
            isOpen: true,
            title: 'Status not changed',
            message: error.response?.data?.detail || error.message || 'Please try again.',
            variant: 'error',
          });
        }
      },
    });
  };

  const activeCount = departments.filter((department) => department.isActive).length;
  const mappedCount = departments.filter((department) => department.parentId).length;
  const eligibleInitialAdmins = users.filter((user) => user.role === 'user' && user.isActive !== false);

  return (
    <section className="garage-departments-panel">
      <div className="garage-departments-heading">
        <div>
          <span className="garage-eyebrow">Access governance</span>
          <h2>Departments</h2>
          <p>Maintain the Workday business units that control request, tool, and metrics scope.</p>
        </div>
        <div className="garage-departments-heading-actions">
          <button className="garage-btn garage-btn-secondary" onClick={load} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'garage-spin' : ''} />
            Refresh
          </button>
          {canManage && (
            <button className="garage-btn garage-btn-primary" onClick={openCreate}>
              <Plus size={16} />
              Add department
            </button>
          )}
        </div>
      </div>

      {!canManage && (
        <div className="garage-departments-notice">
          <Lock size={18} />
          <div>
            <strong>Read-only department directory</strong>
            <span>Only a Full Admin can create, edit, deactivate, or reactivate departments.</span>
          </div>
        </div>
      )}

      <div className="garage-department-stats">
        <div><span>Total</span><strong>{departments.length}</strong></div>
        <div><span>Active</span><strong>{activeCount}</strong></div>
        <div><span>Mapped to personas</span><strong>{mappedCount}</strong></div>
        <div><span>Needs mapping</span><strong>{departments.length - mappedCount}</strong></div>
      </div>

      <div className="garage-departments-toolbar">
        <label className="garage-departments-search">
          <Search size={17} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search department or persona…"
          />
        </label>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <span className="garage-departments-result-count">{visibleDepartments.length} results</span>
      </div>

      <div className="garage-departments-table">
        <table>
          <thead>
            <tr>
              <th>Department</th>
              <th>Parent persona</th>
              <th>Assigned users</th>
              <th>Scoped roles</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {!loading && visibleDepartments.map((department) => {
              const counts = countsByDepartment[department.id] || { users: 0, admins: 0, reviewers: 0 };
              const persona = personaById[department.parentId];
              return (
                <tr key={department.id}>
                  <td>
                    <div className="garage-department-name">
                      <span className="garage-department-icon"><Building2 size={17} /></span>
                      <span>
                        <strong>{department.label}</strong>
                        <small>{department.description || 'Workday business unit'}</small>
                      </span>
                    </div>
                  </td>
                  <td>
                    {persona ? (
                      <span className="garage-persona-chip">{persona.label}</span>
                    ) : (
                      <span className="garage-department-warning">Mapping required</span>
                    )}
                  </td>
                  <td><strong>{counts.users}</strong></td>
                  <td>
                    <span className="garage-department-role-count">
                      {counts.admins} admin{counts.admins === 1 ? '' : 's'} · {counts.reviewers} reviewer{counts.reviewers === 1 ? '' : 's'}
                    </span>
                  </td>
                  <td>
                    <span className={`garage-department-status ${department.isActive ? 'is-active' : 'is-inactive'}`}>
                      {department.isActive && <CheckCircle2 size={13} />}
                      {department.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {canManage && (
                      <div className="garage-department-actions">
                        <button onClick={() => openEdit(department)} title="Edit department">
                          <Edit2 size={15} />
                        </button>
                        <button
                          className={department.isActive ? 'is-danger' : ''}
                          onClick={() => requestStatusChange(department)}
                        >
                          {department.isActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && <div className="garage-departments-empty">Loading departments…</div>}
        {!loading && visibleDepartments.length === 0 && (
          <div className="garage-departments-empty">No departments match these filters.</div>
        )}
      </div>

      {showForm && (
        <div className="garage-admin-drawer-backdrop" onClick={() => setShowForm(false)}>
          <aside className="garage-admin-drawer garage-department-drawer" onClick={(event) => event.stopPropagation()}>
            <div className="garage-admin-drawer-header">
              <div>
                <span className="garage-eyebrow">Department governance</span>
                <h2>{editing ? 'Edit department' : 'Add department'}</h2>
                <p>
                  {editing
                    ? 'Update the department name or description. Historical relationships remain intact.'
                    : 'Map this business unit to the persona that controls its scope.'}
                </p>
              </div>
              <button onClick={() => setShowForm(false)} aria-label="Close"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="garage-department-form">
              {!editing && (
                <div className="garage-form-field">
                  <label>Workday business unit / internal value *</label>
                  <input
                    value={form.value}
                    onChange={(event) => setForm({ ...form, value: event.target.value })}
                    required
                    placeholder="e.g. Field Sales"
                  />
                  <small>This identity is permanent so existing user relationships remain intact.</small>
                </div>
              )}
              <div className="garage-form-field">
                <label>{editing ? 'Department name' : 'Display name'} *</label>
                <input
                  value={form.label}
                  onChange={(event) => setForm({ ...form, label: event.target.value })}
                  required
                  placeholder="e.g. Field Sales"
                />
              </div>
              {!editing && (
                <>
                  <div className="garage-form-field">
                    <label>Parent persona *</label>
                    <select
                      value={form.parentId}
                      onChange={(event) => setForm({ ...form, parentId: event.target.value })}
                      required
                    >
                      <option value="">Select persona…</option>
                      {personas.filter((persona) => persona.isActive).map((persona) => (
                        <option key={persona.id} value={persona.id}>{persona.label}</option>
                      ))}
                    </select>
                    <small>Requests and tools are scoped by this parent persona.</small>
                  </div>
                  <div className="garage-form-field">
                    <label>Initial Persona Admin *</label>
                    <select
                      value={form.initialAdminEmail}
                      onChange={(event) => setForm({ ...form, initialAdminEmail: event.target.value })}
                      required
                    >
                      <option value="">Select an existing user…</option>
                      {eligibleInitialAdmins.map((user) => (
                        <option key={user.userEmail} value={user.userEmail}>
                          {user.displayName || user.userEmail} · {user.userEmail}
                        </option>
                      ))}
                    </select>
                    <small>The selected user will receive Persona Admin access for the department’s mapped persona.</small>
                  </div>
                </>
              )}
              <div className="garage-form-field">
                <label>Description *</label>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  rows={4}
                  required
                  placeholder="What this department owns in AI Garage"
                />
              </div>
              <div className="garage-admin-drawer-actions">
                <button type="button" className="garage-btn garage-btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="garage-btn garage-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save department' : 'Add department'}
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}

      <ConfirmModal
        {...confirm}
        onCancel={() => setConfirm({ isOpen: false })}
      />
      <AlertModal
        {...alert}
        onClose={() => setAlert({ isOpen: false })}
      />
    </section>
  );
};

export default GarageDepartmentsAdminPanel;
